import { useTranslator } from "@eo-locale/react";

import {
  Checkbox,
  Field,
  HTMLWrapper,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import type { ChangeEvent } from "react";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface TosQuestionProps {
  question: Partial<Question>;
  isAsterixRequired?: boolean;
}

export const validate = async (answer: "0" | "1"): Promise<string[]> => {
  return answer === "1" ? [] : ["accepted"];
};

const TosQuestion = ({
  question,
  isAsterixRequired = true
}: TosQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || 0;

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.checked ? "1" : ""
      })
    );
  };

  return (
    <Field.Root
      my={4}
      invalid={error.length > 0}
      required={question.required && isAsterixRequired}
      disabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Checkbox
        id={`${question.id}`}
        fontWeight="semibold"
        onCheckedChange={(details) =>
          handleChange({
            target: { checked: details.checked === true }
          } as ChangeEvent<HTMLInputElement>)
        }
        data-testid="tosQuestion-checkbox"
        checked={answer === "1"}
        invalid={error.length > 0}
        disabled={status === "saving"}
        size={{ base: "lg", lg: "md" }}
        name="tosQuestion-checkbox"
        css={{
          "> .chakra-checkbox__control": { alignSelf: "baseline", mt: "4px" }
        }}
      >
        <Field.Label m={0} htmlFor={`${question.id}`}>
          <HTMLWrapper html={question.question} />
          {question.required && (
            <VisuallyHidden>{`* ${translate(
              "common.required"
            )}`}</VisuallyHidden>
          )}
        </Field.Label>
      </Checkbox>
      {error.length > 0 && (
        <Field.ErrorText data-testid="tosQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
    </Field.Root>
  );
};

export default TosQuestion;
